import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Badge, Tooltip, Text, Box, HStack, Button, Icon } from "@chakra-ui/react";
import { FiCopy } from "react-icons/fi";
import { DarwinTool, DarwinToolListWrapper, DarwinToolType } from "build-ts/darwin_agent";
import { getToolsList, duplicateTools } from "../../services/EvaServices";
import Table, { TableColumn, TableFilter, TablePagination } from "../common/Table";
import { getToolTypeColorForDarwinEva } from "utils/helperFuncUtils";
import { useNavigate } from "react-router-dom";


const ToolTable: React.FC = () => {
  const hasMounted = useRef(false);
  const navigate = useNavigate();

  // State for data
  const [tools, setTools] = useState<DarwinTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<TablePagination>({
    total: 0,
    offset: 0,
    has_more: false,
  });
  const [limit] = useState(50);

  // Tool filters
  const [filters, setFilters] = useState<Record<string, string>>({
    search: "",
    type: "all",
  });
  const filtersRef = useRef(filters);

  // Keep ref in sync with state
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [sortBy, setSortBy] = useState("created_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set());

  // Fetch tools from Darwin API
  const fetchTools = useCallback(async (
      reset = false,
      currentSortBy = "created_date",
      currentSortOrder = "asc",
      currentFilters: Record<string, string> = filters,
      newOffset?: number
  ) => {
    try {
      if (reset) {
        setLoading(true);
      }
      setError(null);

      const currentOffset = newOffset !== undefined ? newOffset : (reset ? 0 : pagination.offset || 0);
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: currentOffset.toString(),
        sort_by: currentSortBy,
        sort_order: currentSortOrder,
      });

      if (currentFilters.search && currentFilters.search.trim()) {
        params.append("search_string", currentFilters.search);
      }
      if (currentFilters.type && currentFilters.type !== "all") {
        params.append("tool_type", currentFilters.type);
      }

      const result: DarwinToolListWrapper = DarwinToolListWrapper.fromJSON(await getToolsList(params));

      if (result.tools) {
        // Always replace the tools list, never append
        setTools(result.tools);

        if (result.total) {
          setPagination({
            total: result.total,
            has_more: (currentOffset + limit) < result.total,
            offset: currentOffset,
          });
        }
      } else {
        // API succeeded but no tools returned
        setTools([]);
        setPagination({
          total: 0,
          has_more: false,
          offset: currentOffset,
        });
      }
    } catch (err) {
      const errorMsg = `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [limit, pagination.offset, filters]);

  // Load data on component mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      const loadData = async () => {
        await fetchTools(true);
      };
      loadData();
    }
  }, [fetchTools]);

  // Table event handlers
  const handlePageChange = useCallback(async (offset: number) => {
    await fetchTools(false, sortBy, sortOrder, filters, offset);
  }, [fetchTools, sortBy, sortOrder, filters]);

  const handleSort = useCallback(async (newSortBy: string, newSortOrder: "asc" | "desc") => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    await fetchTools(true, newSortBy, newSortOrder, filters, 0);
  }, [fetchTools, filters]);

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    // Also update the ref immediately
    filtersRef.current = newFilters;
  }, []);

  const handleApplyFilters = useCallback(async () => {
    await fetchTools(true, sortBy, sortOrder, filtersRef.current, 0);
  }, [fetchTools, sortBy, sortOrder]);

  const handleClearFilters = useCallback(async () => {
    const clearedFilters = { search: "", type: "all" };
    setFilters(clearedFilters);
    filtersRef.current = clearedFilters;
    setSortBy("created_date");
    setSortOrder("asc");
    await fetchTools(true, "created_date", "asc", clearedFilters, 0);
  }, [fetchTools]);

  const handleToolRowClick = useCallback(
    (tool: DarwinTool) => {
      if (tool.header?.id) {
        navigate(`/eva/tool/${tool.header.id}`);
      }
    },
    [navigate]
  );

  const handleSelectionChange = useCallback((newSelectedTools: Set<string>) => {
    setSelectedTools(newSelectedTools);
  }, []);

  const handleDuplicateSelected = async () => {
    if (selectedTools.size === 0) return;

    try {
      const toolIds = Array.from(selectedTools).map((toolId) => Number(toolId));
      const duplicated = await duplicateTools(toolIds);
      if (duplicated) {
        setSelectedTools(new Set());
        await fetchTools(true, sortBy, sortOrder, filtersRef.current, 0);
      }
    } catch (error) {
      console.error("Error duplicating tools:", error);
    }
  };

  // Get tool types from DarwinToolType enum
  const toolTypes = useMemo(() => {
    return Object.values(DarwinToolType).filter(
      value => typeof value === "string" && value !== "UNRECOGNIZED" && value !== "UNKNOWN_TOOL_TYPE"
    ) as string[];
  }, []);

  // Table configuration
  const tableColumns: TableColumn<DarwinTool>[] = [
    {
      key: "name",
      label: "Internal Name",
      sortable: true,
      className: "eva-name-column",
      render: (tool) => (
        <Tooltip label={tool.header?.name} placement="top">
          <Text fontSize="sm" color="gray.700" noOfLines={2} wordBreak="break-word" mb={0} whiteSpace="normal" fontWeight="600">
            {tool.header?.name}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "type",
      label: "Type",
      className: "eva-type-column",
      render: (tool) => {
        const toolType = tool.header?.type as keyof typeof DarwinToolType;
        return (
          <Badge colorScheme={getToolTypeColorForDarwinEva(DarwinToolType[toolType])}>
            {tool.header?.type || "Unknown"}
          </Badge>
        );
      },
    },
    {
      key: "is_response_tool",
      label: "Response?",
      className: "eva-is-response-tool-column",
      render: (tool) => (
        <Badge colorScheme={tool.header?.is_response_tool ? "green" : "red"} size="sm">
          {tool.header?.is_response_tool ? "True" : "False"}
        </Badge>
      ),
    },
    {
      key: "agentic_name",
      label: "Name to Agent",
      sortable: true,
      className: "eva-agentic-name-column",
      render: (tool) => (
        <Tooltip label={tool?.agentic_name} placement="top">
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1} wordBreak="break-word" mb={0}>
            {tool?.agentic_name}
          </Text>
        </Tooltip>
      ),
    },
    {
      key: "agentic_description",
      label: "Description to Agent",
      sortable: true,
      className: "eva-agentic-description-column",
      render: (tool) => (
        <Tooltip label={tool?.agentic_description} placement="top">
           <Text fontSize="sm" color="gray.700" noOfLines={2} wordBreak="break-word" mb={0} whiteSpace="normal">
            {tool?.agentic_description}
          </Text>
        </Tooltip>
      ),
    },
  ];

  const tableFilters: TableFilter[] = [
    {
      key: "search",
      type: "text",
      label: "Search",
      placeholder: "Search tools...",
      width: "300px",
    },
    {
      key: "type",
      type: "select",
      label: "Type",
      options: [
        { value: "all", label: "All Types" },
        ...toolTypes.map((type: string) => ({ value: type, label: type })),
      ],
      width: "200px",
    },
  ];

  const headerActions = selectedTools.size > 0 ? (
    <HStack spacing={4}>
      <Button
        onClick={handleDuplicateSelected}
        colorScheme="blue"
        size="sm"
        leftIcon={<Icon as={FiCopy} />}
      >
        Duplicate
      </Button>
      <Button
        onClick={() => setSelectedTools(new Set())}
        variant="outline"
        size="sm"
      >
        Cancel
      </Button>
    </HStack>
  ) : null;

  return (
    <Box height="100%" overflowY="hidden">
      <Table
        data={tools}
        columns={tableColumns}
        loading={loading}
        error={error}
        pagination={pagination}
        limit={limit}
        onPageChange={handlePageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        filters={tableFilters}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        selectable={true}
        selectedItems={selectedTools}
        onSelectionChange={handleSelectionChange}
        getItemId={(tool) => tool.header?.id || ""}
        onRowClick={handleToolRowClick}
        headerActions={headerActions}
        emptyMessage="No tools found"
        emptySubMessage="Try adjusting your search criteria"
      />
    </Box>
  );
};

export default ToolTable;
